import type { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { GameSettings } from "../game/GameConfig";
import { ChipSoundSynthesizer, isChipSoundId } from "./ChipSoundSynthesizer";
import { DungeonMusicSequencer } from "./DungeonMusicSequencer";

export type MusicMode = "none" | "dungeon";

export type SoundId =
  | "rifle_fire"
  | "pistol_fire"
  | "shotgun_fire"
  | "shotgun_pump"
  | "weapon_reload"
  | "dry_fire"
  | "enemy_alert"
  | "enemy_attack"
  | "enemy_pain"
  | "enemy_death_guard"
  | "enemy_death_stalker"
  | "player_hurt"
  | "door_open"
  | "door_close"
  | "switch"
  | "pickup_health"
  | "pickup_ammo"
  | "pickup_weapon"
  | "secret_found"
  | "wall_impact"
  | "enemy_impact"
  | "ui_hover"
  | "ui_click"
  | "level_complete";

const SOUND_IDS: readonly SoundId[] = [
  "rifle_fire",
  "pistol_fire",
  "shotgun_fire",
  "shotgun_pump",
  "weapon_reload",
  "dry_fire",
  "enemy_alert",
  "enemy_attack",
  "enemy_pain",
  "enemy_death_guard",
  "enemy_death_stalker",
  "player_hurt",
  "door_open",
  "door_close",
  "switch",
  "pickup_health",
  "pickup_ammo",
  "pickup_weapon",
  "secret_found",
  "wall_impact",
  "enemy_impact",
  "ui_hover",
  "ui_click",
  "level_complete",
];

export class AudioSystem {
  private context?: AudioContext;
  private masterGain?: GainNode;
  private effectsGain?: GainNode;
  private ambienceGain?: GainNode;
  private chipSynthesizer?: ChipSoundSynthesizer;
  private dungeonMusic?: DungeonMusicSequencer;
  private musicMode: MusicMode = "none";
  private readonly buffers = new Map<SoundId, AudioBuffer>();
  private readonly activeCounts = new Map<SoundId, number>();
  private disposed = false;

  public constructor(private settings: GameSettings) {}

  public async unlock(): Promise<void> {
    if (this.disposed) return;
    const needsPreload = !this.context;
    if (!this.context) {
      this.createContext();
    }
    // Resume in the user gesture, before network / decoding yields activation.
    if (this.context?.state === "suspended") {
      await this.context.resume();
    }
    if (needsPreload) await this.preload();
  }

  public play(id: SoundId, position?: Vector3): void {
    const context = this.context;
    const effectsGain = this.effectsGain;
    if (!context || !effectsGain || context.state !== "running") return;
    const activeCount = this.activeCounts.get(id) ?? 0;
    if (activeCount >= 4) return;
    if (isChipSoundId(id) && this.chipSynthesizer) {
      this.activeCounts.set(id, activeCount + 1);
      const duration = this.chipSynthesizer.play(id, position);
      globalThis.setTimeout(
        () => {
          this.activeCounts.set(id, Math.max(0, (this.activeCounts.get(id) ?? 1) - 1));
        },
        (duration + 0.08) * 1000,
      );
      return;
    }
    const buffer = this.buffers.get(id);
    if (!buffer) {
      this.playSyntheticFallback(id, position);
      return;
    }

    const source = context.createBufferSource();
    source.buffer = buffer;
    let output: AudioNode = effectsGain;
    if (position) {
      const panner = context.createPanner();
      panner.panningModel = "HRTF";
      panner.distanceModel = "inverse";
      panner.refDistance = 2;
      panner.maxDistance = 45;
      panner.rolloffFactor = 1.2;
      panner.positionX.value = position.x;
      panner.positionY.value = position.y;
      panner.positionZ.value = position.z;
      panner.connect(effectsGain);
      output = panner;
    }
    source.connect(output);
    this.activeCounts.set(id, activeCount + 1);
    source.addEventListener("ended", () => {
      this.activeCounts.set(id, Math.max(0, (this.activeCounts.get(id) ?? 1) - 1));
      source.disconnect();
    });
    source.start();
  }

  public setMusicMode(mode: MusicMode): void {
    this.musicMode = mode;
    this.dungeonMusic?.setEnabled(mode === "dungeon");
  }

  public update(_deltaSeconds: number, listenerPosition?: Vector3, forward?: Vector3): void {
    const listener = this.context?.listener;
    if (listener && listenerPosition) {
      listener.positionX.value = listenerPosition.x;
      listener.positionY.value = listenerPosition.y;
      listener.positionZ.value = listenerPosition.z;
      if (forward) {
        listener.forwardX.value = forward.x;
        listener.forwardY.value = forward.y;
        listener.forwardZ.value = forward.z;
        listener.upX.value = 0;
        listener.upY.value = 1;
        listener.upZ.value = 0;
      }
    }
    this.dungeonMusic?.update();
  }

  public applySettings(settings: GameSettings): void {
    this.settings = settings;
    if (this.context && this.masterGain && this.effectsGain && this.ambienceGain) {
      const now = this.context.currentTime;
      this.masterGain.gain.setTargetAtTime(settings.masterVolume, now, 0.04);
      this.effectsGain.gain.setTargetAtTime(settings.effectsVolume, now, 0.04);
      this.ambienceGain.gain.setTargetAtTime(settings.ambienceVolume, now, 0.08);
    }
  }

  public dispose(): void {
    this.disposed = true;
    this.dungeonMusic?.dispose();
    this.buffers.clear();
    this.activeCounts.clear();
    if (this.context) void this.context.close();
    this.chipSynthesizer = undefined;
    this.dungeonMusic = undefined;
    this.context = undefined;
  }

  private createContext(): void {
    const context = new AudioContext();
    const masterGain = context.createGain();
    const effectsGain = context.createGain();
    const ambienceGain = context.createGain();
    masterGain.gain.value = this.settings.masterVolume;
    effectsGain.gain.value = this.settings.effectsVolume;
    ambienceGain.gain.value = this.settings.ambienceVolume;
    effectsGain.connect(masterGain);
    ambienceGain.connect(masterGain);
    masterGain.connect(context.destination);
    this.context = context;
    this.masterGain = masterGain;
    this.effectsGain = effectsGain;
    this.ambienceGain = ambienceGain;
    this.chipSynthesizer = new ChipSoundSynthesizer(context, effectsGain);
    this.dungeonMusic = new DungeonMusicSequencer(context, ambienceGain);
    this.dungeonMusic.setEnabled(this.musicMode === "dungeon");
  }

  private async preload(): Promise<void> {
    const context = this.context;
    if (!context) return;
    await Promise.allSettled(
      SOUND_IDS.filter((id) => !isChipSoundId(id)).map(async (id) => {
        const publicBaseUrl = new URL(import.meta.env.BASE_URL, document.baseURI);
        const response = await fetch(new URL(`audio/${id}.ogg`, publicBaseUrl));
        if (!response.ok) return;
        const buffer = await context.decodeAudioData(await response.arrayBuffer());
        this.buffers.set(id, buffer);
      }),
    );
  }

  private playSyntheticFallback(id: SoundId, position?: Vector3): void {
    const context = this.context;
    const effectsGain = this.effectsGain;
    if (!context || !effectsGain) return;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const isLow = id.includes("shotgun") || id.includes("death") || id.includes("door");
    oscillator.type = id.includes("pickup") || id.includes("ui") ? "sine" : "sawtooth";
    oscillator.frequency.setValueAtTime(isLow ? 85 : 210, context.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(isLow ? 32 : 95, context.currentTime + 0.1);
    gain.gain.setValueAtTime(0.06, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.12);
    if (position) {
      const panner = context.createPanner();
      panner.positionX.value = position.x;
      panner.positionY.value = position.y;
      panner.positionZ.value = position.z;
      oscillator.connect(gain).connect(panner).connect(effectsGain);
    } else {
      oscillator.connect(gain).connect(effectsGain);
    }
    oscillator.start();
    oscillator.stop(context.currentTime + 0.13);
  }
}
